// Página de listagem e edição de empresas do usuário
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Eye, Building, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getEmpresaById, updateEmpresa } from '../../../services/empresasService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCNPJ, formatDate } from '../../../utils/formatters';
import type { Empresa } from '../../../types';

export function Empresa() {
  const { empresas, empresaSelecionada, setEmpresaSelecionada, carregarEmpresas, loading: empresasLoading } = useEmpresa();
  const { canUpdate } = usePermissions();
  const [empresaEditando, setEmpresaEditando] = useState<Empresa | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    inscricaoEstadual: '',
    email: '',
    telefone: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
  });

  // Carregar empresas quando o componente montar
  useEffect(() => {
    carregarEmpresas();
  }, []);

  // Carregar dados da empresa quando abrir modal de edição
  useEffect(() => {
    if (empresaEditando) {
      carregarDadosEmpresa();
    }
  }, [empresaEditando]);

  const carregarDadosEmpresa = async () => {
    if (!empresaEditando) return;
    
    try {
      setLoading(true);
      setError(null);
      const empresa = await getEmpresaById(empresaEditando.id);
      
      if (empresa) {
        setFormData({
          razaoSocial: empresa.razaoSocial,
          nomeFantasia: empresa.nomeFantasia,
          cnpj: empresa.cnpj,
          inscricaoEstadual: empresa.inscricaoEstadual,
          email: empresa.email,
          telefone: empresa.telefone,
          cep: empresa.endereco.cep,
          logradouro: empresa.endereco.logradouro,
          numero: empresa.endereco.numero,
          complemento: empresa.endereco.complemento || '',
          bairro: empresa.endereco.bairro,
          cidade: empresa.endereco.cidade,
          estado: empresa.endereco.estado,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados da empresa');
      console.error('Erro ao carregar empresa:', err);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (empresa?: Empresa) => {
    setEmpresaEditando(empresa || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setEmpresaEditando(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaEditando) {
      setError('Nenhuma empresa selecionada');
      return;
    }

    if (!canUpdate) {
      setError('Você não tem permissão para atualizar os dados da empresa');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await updateEmpresa(empresaEditando.id, {
        razaoSocial: formData.razaoSocial,
        nomeFantasia: formData.nomeFantasia,
        cnpj: formData.cnpj,
        inscricaoEstadual: formData.inscricaoEstadual,
        email: formData.email,
        telefone: formData.telefone,
        endereco: {
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
      });

      // Recarregar lista de empresas
      await carregarEmpresas();
      
      // Se a empresa editada é a selecionada, atualizar no contexto
      if (empresaSelecionada?.id === empresaEditando.id) {
        const empresaAtualizada = await getEmpresaById(empresaEditando.id);
        if (empresaAtualizada) {
          setEmpresaSelecionada(empresaAtualizada);
        }
      }
      
      alert('Dados da empresa atualizados com sucesso!');
      fecharModal();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar dados da empresa');
      console.error('Erro ao salvar empresa:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const selecionarEmpresa = (empresa: Empresa) => {
    setEmpresaSelecionada(empresa);
  };

  if (empresasLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Empresas' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Empresas' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Empresas</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      {error && !isModalOpen && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Lista de Empresas */}
      <Card>
        {empresas.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">Nenhuma empresa cadastrada</p>
            <Button onClick={() => abrirModal()} variant="primary">
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Primeira Empresa
            </Button>
          </div>
        ) : (
          <Table
            data={empresas}
            columns={[
              {
                key: 'nomeFantasia',
                label: 'Nome Fantasia',
                render: (item: Empresa) => (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{item.nomeFantasia}</span>
                  </div>
                ),
              },
              {
                key: 'razaoSocial',
                label: 'Razão Social',
              },
              {
                key: 'cnpj',
                label: 'CNPJ',
                render: (item: Empresa) => formatCNPJ(item.cnpj),
              },
              {
                key: 'email',
                label: 'Email',
              },
              {
                key: 'telefone',
                label: 'Telefone',
              },
              {
                key: 'actions',
                label: 'Ações',
                render: (item: Empresa) => (
                  <div className="flex items-center gap-2">
                    {empresaSelecionada?.id === item.id && (
                      <span className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded">
                        Selecionada
                      </span>
                    )}
                    <button
                      onClick={() => selecionarEmpresa(item)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Selecionar empresa"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => abrirModal(item)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                      title="Editar empresa"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Modal de Edição/Criação */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={empresaEditando ? 'Editar Empresa' : 'Nova Empresa'}
        size="lg"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loading />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Dados Cadastrais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Razão Social *"
                  value={formData.razaoSocial}
                  onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Nome Fantasia *"
                  value={formData.nomeFantasia}
                  onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="CNPJ *"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate || !!empresaEditando}
                />
                <Input
                  label="Inscrição Estadual *"
                  value={formData.inscricaoEstadual}
                  onChange={(e) => setFormData({ ...formData, inscricaoEstadual: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Telefone *"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Endereço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="CEP *"
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <div className="md:col-span-2">
                  <Input
                    label="Logradouro *"
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                    required
                    disabled={isSaving || !canUpdate}
                  />
                </div>
                <Input
                  label="Número *"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Complemento"
                  value={formData.complemento}
                  onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Bairro *"
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Cidade *"
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                />
                <Input
                  label="Estado *"
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  required
                  disabled={isSaving || !canUpdate}
                  maxLength={2}
                />
              </div>
            </Card>

            <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={fecharModal} disabled={isSaving}>
                Cancelar
              </Button>
              {canUpdate && (
                <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
                  {isSaving ? 'Salvando...' : 'Salvar Dados'}
                </Button>
              )}
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
